import { useCallback, useEffect, useRef, useState } from 'react'

// Copy-to-clipboard with a transient acknowledgement, in one place.
//
// The portal had grown five of these, and they had already drifted in the ways
// these helpers always do: one acknowledged before the write resolved (so a
// blocked clipboard still claimed "Copied"), one left its reset timer running
// past unmount, and they disagreed on how long the acknowledgement lasts.
//
// The behaviour settled on here: acknowledge only what actually landed, never
// throw at a caller, and never leave a timer behind. A denied clipboard —
// insecure origin, permissions policy — leaves the control inert rather than
// lying, which is why `isSupported` is offered for surfaces that would rather
// hide the control than show one that does nothing.

const DEFAULT_RESET_MS = 2000

interface Clipboard {
  copied: boolean
  copy: (text: string) => void
  /** False where the API is unavailable, e.g. an insecure origin. */
  isSupported: boolean
}

export const useCopyToClipboard = (
  resetMs: number = DEFAULT_RESET_MS
): Clipboard => {
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => () => clearTimeout(timer.current), [])

  const copy = useCallback(
    (text: string) => {
      void navigator.clipboard
        ?.writeText(text)
        .then(() => {
          setCopied(true)
          clearTimeout(timer.current)
          timer.current = setTimeout(() => setCopied(false), resetMs)
        })
        .catch(() => {
          // Denied or unavailable: the control simply does not acknowledge.
        })
    },
    [resetMs]
  )

  // Read per render rather than captured once: jsdom and a permissions prompt
  // can both furnish the API after the first paint.
  return { copied, copy, isSupported: Boolean(navigator.clipboard) }
}
